import { useEffect, useState, useMemo, useCallback } from "react";
import { Pose } from "@mediapipe/pose";
import { drawConnectors, drawLandmarks } from "@mediapipe/drawing_utils";
import { POSE_CONNECTIONS } from "@mediapipe/pose";
import { Camera } from "@mediapipe/camera_utils";
import { calculateDistance, calculateAngle } from "../utils/geometry";

// Static boolean to enable test data mode
const USE_TEST_DATA = false;

const useBlazePose = ({
  videoRef,
  canvasRef,
  playPunchSound,
  playDodgeSound,
  playRestSound,
  playBeaten,
}) => {
  const [poseData, setPoseData] = useState(null);

  const [leftPunchActive, setLeftPunchActive] = useState(false);
  const [rightPunchActive, setRightPunchActive] = useState(false);

  const [actions, setActions] = useState({
    left: { punched: false },
    right: { punched: false },
  });

  // Test data state
  const [testPunchCycle, setTestPunchCycle] = useState("none"); // 'none', 'left', 'right'
  const [testPosition, setTestPosition] = useState({ x: 0.5, y: 0.4 });

  const consumeAction = useCallback((side) => {
    if (side === "left") {
      setActions((prev) => ({ ...prev, left: { punched: false } }));
    } else if (side === "right") {
      setActions((prev) => ({ ...prev, right: { punched: false } }));
    }
  }, []);

  // Test data generation useEffect
  useEffect(() => {
    if (!USE_TEST_DATA) return;

    const interval = setInterval(() => {
      // Simple cycle for punches: none -> left -> none -> right
      const cycle = ["none", "left", "none", "right"];
      const current = cycle[Math.floor(Date.now() / 100) % cycle.length];
      setTestPunchCycle(current);
    }, 500); // Change every 0.5s for faster testing

    return () => clearInterval(interval);
  }, []);

  const { conditions, poses, position } = useMemo(() => {
    // If in test mode, return test data instead of real analysis
    if (USE_TEST_DATA) {
      const isLeftPunch = testPunchCycle === "left";
      const isRightPunch = testPunchCycle === "right";

      return {
        conditions: {
          left: {
            proximity: isLeftPunch,
            elbowPos: isLeftPunch,
            elbowStraight: isLeftPunch,
          },
          right: {
            proximity: isRightPunch,
            elbowPos: isRightPunch,
            elbowStraight: isRightPunch,
          },
        },
        poses: {
          left: {
            block: !isLeftPunch,
            straight: isLeftPunch,
            loose: false,
          },
          right: {
            block: !isRightPunch,
            straight: isRightPunch,
            loose: false,
          },
        },
        position: testPosition, // Mock center position
      };
    }

    let newConditions = {
      left: { proximity: false, elbowPos: false, elbowStraight: false },
      right: { proximity: false, elbowPos: false, elbowStraight: false },
    };
    let newPoses = {
      left: { block: false, straight: false, loose: true },
      right: { block: false, straight: false, loose: true },
    };
    let newPosition = { x: null, y: null };

    if (
      poseData &&
      poseData.poseLandmarks &&
      poseData.poseWorldLandmarks &&
      canvasRef.current
    ) {
      const {
        poseLandmarks: screenLandmarks,
        poseWorldLandmarks: worldLandmarks,
      } = poseData;

      // --- Head Position Calculation ---
      const nose = screenLandmarks[0];
      const leftEye = screenLandmarks[2];
      const rightEye = screenLandmarks[5];
      const leftEar = screenLandmarks[7];
      const rightEar = screenLandmarks[8];
      const mouthLeft = screenLandmarks[9];
      const mouthRight = screenLandmarks[10];

      let headX = null;
      let headY = null;
      const visibilityThreshold = 0.5;

      // 1. Try midpoint of ears for stable head center
      if (
        leftEar?.visibility > visibilityThreshold &&
        rightEar?.visibility > visibilityThreshold
      ) {
        headX = (leftEar.x + rightEar.x) / 2;
        headY = (leftEar.y + rightEar.y) / 2;
      }
      // 2. Fallback to midpoint of eyes
      else if (
        leftEye?.visibility > visibilityThreshold &&
        rightEye?.visibility > visibilityThreshold
      ) {
        headX = (leftEye.x + rightEye.x) / 2;
        headY = (leftEye.y + rightEye.y) / 2;
      }
      // 3. Fallback to midpoint of mouth
      else if (
        mouthLeft?.visibility > visibilityThreshold &&
        mouthRight?.visibility > visibilityThreshold
      ) {
        headX = (mouthLeft.x + mouthRight.x) / 2;
        headY = (mouthLeft.y + mouthRight.y) / 2;
      }
      // 4. Fallback to nose
      else if (nose?.visibility > visibilityThreshold) {
        headX = nose.x;
        headY = nose.y;
      }

      if (headX !== null) {
        newPosition = { x: headX, y: headY };
      }

      const screenLeftWrist = screenLandmarks[15];
      const screenRightWrist = screenLandmarks[16];
      const screenLeftShoulder = screenLandmarks[11];
      const screenRightShoulder = screenLandmarks[12];
      const screenLeftHip = screenLandmarks[23];
      const screenRightHip = screenLandmarks[24];

      const worldLeftShoulder = worldLandmarks[11];
      const worldRightShoulder = worldLandmarks[12];
      const worldLeftElbow = worldLandmarks[13];
      const worldRightElbow = worldLandmarks[14];
      const worldLeftWrist = worldLandmarks[15];
      const worldRightWrist = worldLandmarks[16];
      const worldLeftHip = worldLandmarks[23];
      const worldRightHip = worldLandmarks[24];

      const hasValidLandmarks = [
        screenLeftWrist,
        screenRightWrist,
        screenLeftShoulder,
        screenRightShoulder,
        worldLeftShoulder,
        worldRightShoulder,
        worldLeftElbow,
        worldRightElbow,
        worldLeftWrist,
        worldRightWrist,
      ].every((landmark) => landmark && landmark.visibility > 0.3);

      if (hasValidLandmarks) {
        const leftTorsoLength = calculateDistance(
          worldLeftShoulder,
          worldLeftHip
        );
        const rightTorsoLength = calculateDistance(
          worldRightShoulder,
          worldRightHip
        );
        const torsoUnit = (leftTorsoLength + rightTorsoLength) / 2;
        const y_threshold_offset = 0.2 * torsoUnit;

        const leftTorsoPixels = Math.sqrt(
          Math.pow(
            (screenLeftShoulder.x - screenLeftHip.x) * canvasRef.current.width,
            2
          ) +
            Math.pow(
              (screenLeftShoulder.y - screenLeftHip.y) *
                canvasRef.current.height,
              2
            )
        );
        const rightTorsoPixels = Math.sqrt(
          Math.pow(
            (screenRightShoulder.x - screenRightHip.x) *
              canvasRef.current.width,
            2
          ) +
            Math.pow(
              (screenRightShoulder.y - screenRightHip.y) *
                canvasRef.current.height,
              2
            )
        );
        const avgTorsoPixels = (leftTorsoPixels + rightTorsoPixels) / 2;
        const radius = avgTorsoPixels * (2 / 5);

        const mouthCenterX =
          ((mouthLeft.x + mouthRight.x) / 2) * canvasRef.current.width;
        const mouthCenterY =
          ((mouthLeft.y + mouthRight.y) / 2) * canvasRef.current.height;

        const distLeft = Math.sqrt(
          Math.pow(
            screenLeftWrist.x * canvasRef.current.width - mouthCenterX,
            2
          ) +
            Math.pow(
              screenLeftWrist.y * canvasRef.current.height - mouthCenterY,
              2
            )
        );
        const distRight = Math.sqrt(
          Math.pow(
            screenRightWrist.x * canvasRef.current.width - mouthCenterX,
            2
          ) +
            Math.pow(
              screenRightWrist.y * canvasRef.current.height - mouthCenterY,
              2
            )
        );
        newConditions.left.proximity = distLeft < radius;
        newConditions.right.proximity = distRight < radius;

        const leftThresholdY = worldLeftShoulder.y + y_threshold_offset;
        const rightThresholdY = worldRightShoulder.y + y_threshold_offset;
        newConditions.left.elbowPos = worldLeftElbow.y < leftThresholdY;
        newConditions.right.elbowPos = worldRightElbow.y < rightThresholdY;

        const leftElbowAngle = calculateAngle(
          worldLeftShoulder,
          worldLeftElbow,
          worldLeftWrist
        );
        const rightElbowAngle = calculateAngle(
          worldRightShoulder,
          worldRightElbow,
          worldRightWrist
        );
        newConditions.left.elbowStraight = (leftElbowAngle / 180) * 100 > 70;
        newConditions.right.elbowStraight = (rightElbowAngle / 180) * 100 > 70;

        // --- Poses ---
        const { left, right } = newConditions;
        newPoses.left.block =
          left.proximity && !left.elbowPos && !left.elbowStraight;
        newPoses.right.block =
          right.proximity && !right.elbowPos && !right.elbowStraight;

        newPoses.left.straight = left.elbowPos && left.elbowStraight;
        newPoses.right.straight = right.elbowPos && right.elbowStraight;

        newPoses.left.loose = !newPoses.left.block && !newPoses.left.straight;
        newPoses.right.loose =
          !newPoses.right.block && !newPoses.right.straight;
      }
    }

    return {
      conditions: newConditions,
      poses: newPoses,
      position: newPosition,
    };
  }, [poseData, canvasRef, testPunchCycle, testPosition]);

  useEffect(() => {
    if (USE_TEST_DATA) {
      // In test mode, mimic the one-shot behavior of the real mode
      const isLeftPunchCycle = testPunchCycle === "left";
      const isRightPunchCycle = testPunchCycle === "right";

      // Left arm logic for test mode
      if (isLeftPunchCycle && !leftPunchActive) {
        setActions((prev) => ({ ...prev, left: { punched: true } }));
        setLeftPunchActive(true);
      } else if (!isLeftPunchCycle && leftPunchActive) {
        setLeftPunchActive(false);
      }

      // Right arm logic for test mode
      if (isRightPunchCycle && !rightPunchActive) {
        setActions((prev) => ({ ...prev, right: { punched: true } }));
        setRightPunchActive(true);
      } else if (!isRightPunchCycle && rightPunchActive) {
        setRightPunchActive(false);
      }
      return; // End here for test mode
    }

    // Original punch logic for real pose detection
    // Left arm logic
    if (poses.left.straight && !leftPunchActive) {
      setActions((prev) => ({ ...prev, left: { punched: true } }));
      setLeftPunchActive(true);
    } else if (!poses.left.straight && leftPunchActive) {
      setLeftPunchActive(false);
    }

    // Right arm logic
    if (poses.right.straight && !rightPunchActive) {
      setActions((prev) => ({ ...prev, right: { punched: true } }));
      setRightPunchActive(true);
    } else if (!poses.right.straight && rightPunchActive) {
      setRightPunchActive(false);
    }
  }, [poses, leftPunchActive, rightPunchActive, testPunchCycle]);

  useEffect(() => {
    // Skip camera/pose setup in test mode
    if (USE_TEST_DATA) {
      return;
    }

    let active = true;
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;
    const canvasCtx = canvasElement.getContext("2d");

    const pose = new Pose({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5.1675469404/${file}`;
      },
    });

    pose.setOptions({
      modelComplexity: 1, // More accurate, but slower. 0=lite, 1=full, 2=heavy
      smoothLandmarks: true, // Reduces jitter
      enableSegmentation: true, // Enable segmentation for better visualization
      smoothSegmentation: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    pose.onResults((results) => {
      if (!active) return;
      setPoseData(results);

      canvasElement.width = videoElement.videoWidth;
      canvasElement.height = videoElement.videoHeight;

      canvasCtx.save();
      canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);

      if (results.poseLandmarks) {
        drawConnectors(canvasCtx, results.poseLandmarks, POSE_CONNECTIONS, {
          color: "#23dd3455",
          lineWidth: 4,
        });
        drawLandmarks(canvasCtx, results.poseLandmarks, {
          color: "#dd556655",
          radius: 6,
        });
      }
      canvasCtx.restore();
    });

    let camera = new Camera(videoElement, {
      onFrame: async () => {
        if (!active) return;
        if (videoElement.readyState >= 3) {
          // HAVE_FUTURE_DATA
          await pose.send({ image: videoElement });
        }
      },
      width: 640,
      height: 480,
    });
    camera.start();

    return () => {
      active = false;
      if (camera) {
        camera.stop();
      }
      pose.close();
    };
  }, [videoRef, canvasRef]);

  return { poses, actions, position, conditions, consumeAction };
};

export default useBlazePose;
